import { Controller } from 'stimulus'

export default class extends Controller {
  static targets = ['amountWrapper', 'amountField', 'splitPaymentsField']

  static values = {
    splitPayments: Boolean,
    amountRemaining: Number,
    purchaseToken: String,
    cartId: Number,
  }

  connect() {
    this.stripeIntentTimeout = null

    // If we're already in split payment mode (e.g., on complete_split_payment page),
    // show the fields immediately
    if (this.splitPaymentsValue) {
      this.showFields()
    }
  }

  disconnect() {
    clearTimeout(this.stripeIntentTimeout)
  }

  showFields() {
    // Show the split payment section
    this.amountWrapperTarget.classList.remove('hidden')

    // Mark the hidden input with value 1 signaling on the backend that this payment is part
    // of a split payment
    this.splitPaymentsFieldTarget.value = '1'

    // Change the text on the submit button to say "Authorize and add another payment"
    this.getSubmitButton().innerHTML = this.getSubmitButton().dataset.authAndAdd
  }

  // Reverse of the function above essentially
  hideFields() {
    this.amountWrapperTarget.classList.add('hidden')
    this.splitPaymentsFieldTarget.value = '0'
    this.getSubmitButton().innerHTML = this.getSubmitButton().dataset.payLabel
  }

  getSubmitButton() {
    return document.querySelector('button.submit-payment')
  }

  updateSubmitButtonFromAmount(e) {
    e.preventDefault()
    e.stopPropagation()

    const amount = parseFloat(e.target.value)

    if (amount > this.amountRemainingValue) {
      this.getSubmitButton().disabled = true
    } else {
      this.getSubmitButton().disabled = false
    }

    if (amount === this.amountRemainingValue) {
      this.getSubmitButton().innerHTML = this.getSubmitButton().dataset.payLabel
    } else {
      this.getSubmitButton().innerHTML =
        this.getSubmitButton().dataset.authAndAdd
    }

    // Recreate Stripe PaymentIntent with new amount (debounced)
    this.recreateStripeIntent(e.target)
  }

  recreateStripeIntent(amountField) {
    // Clear any existing timeout
    clearTimeout(this.stripeIntentTimeout)

    // Debounce by 500ms
    this.stripeIntentTimeout = setTimeout(() => {
      this.performStripeIntentRecreation(amountField)
    }, 500)
  }

  async performStripeIntentRecreation(amountField) {
    const amount = amountField.value

    // Only recreate intent if we have a new Stripe payment method selected
    const stripeProcessorOption = this.getSelectedStripeProcessorOption()
    if (!stripeProcessorOption) return

    // Verify split payments are enabled
    if (this.splitPaymentsFieldTarget.value !== '1') return

    this.showLoading()
    this.disableSubmitButtons()

    const value = stripeProcessorOption.value
    const [paymentProcessorId, paymentProcessorSubtype] = value
      .replace(/pp-/, '')
      .split('-')

    // Cancel the previous PaymentIntent before creating a new one (fire-and-forget)
    const prevIntentId = this.getSubmitButton().dataset.intentId
    if (prevIntentId) {
      const cancelData = new FormData()
      cancelData.append('payment_processor_id', paymentProcessorId)
      cancelData.append('intent_id', prevIntentId)
      const csrfParam = document.querySelector('meta[name=csrf-param]')?.content
      const csrfToken = document.querySelector('meta[name=csrf-token]')?.content
      if (csrfParam && csrfToken) cancelData.append(csrfParam, csrfToken)
      fetch(this.getBaseCartPath() + '/stripe_cancel', {
        method: 'POST',
        body: cancelData,
      })
    }

    const formData = new FormData(
      document.querySelector('form.cart-checkout-payment')
    )
    formData.append('payment_method[payment_processor_id]', paymentProcessorId)
    if (paymentProcessorSubtype) {
      formData.append(
        'payment_method[payment_processor_subtype]',
        paymentProcessorSubtype
      )
    }
    // Replace any amount/split_payments already present on the form so we never
    // send duplicate keys (Rails can turn those into arrays → to_s.to_d => 0).
    formData.set('amount', amount)
    formData.set('split_payments', '1')
    if (this.hasPurchaseTokenValue && this.purchaseTokenValue) {
      formData.set('purchase_token', this.purchaseTokenValue)
    }
    if (this.hasCartIdValue && this.cartIdValue) {
      formData.set('cart_id', String(this.cartIdValue))
    }
    formData.append('_method', 'post')

    try {
      const response = await fetch(this.getBaseCartPath() + '/stripe_setup', {
        method: 'POST',
        body: formData,
      })

      const json = await response.json()

      if (!response.ok) {
        // Surface the server-side validation message (e.g. "Invalid payment amount") to the user
        // instead of a generic HTTP status.
        throw new Error(json.alert || `HTTP error! status: ${response.status}`)
      }

      const submitButton = this.getSubmitButton()
      submitButton.setAttribute('data-client-secret', json.client_secret)
      submitButton.setAttribute('data-intent-id', json.intent_id)
      submitButton.setAttribute('data-intent-type', json.intent_type)

      // Reinitialize Stripe element with new client secret
      await this.reinitializeStripeElement(
        json.publishable_key,
        json.client_secret,
        paymentProcessorSubtype,
        json.automatic_payment_methods,
        json.future_charge,
        json.excluded_wallets
      )

      this.hideLoading()
      this.enableSubmitButtons()
    } catch (error) {
      console.error('Failed to update PaymentIntent:', error)
      this.hideLoading()
      this.enableSubmitButtons()
      alert(
        error.message ||
          'Failed to update payment amount. Please refresh and try again.'
      )
    }
  }

  async reinitializeStripeElement(
    publishableKey,
    clientSecret,
    paymentProcessorSubtype,
    automaticPaymentMethods = false,
    futureCharge = false,
    excludedWallets = []
  ) {
    // Don't reinitialize for bancontact (no stripe element)
    if (paymentProcessorSubtype === 'bancontact') return

    // Clear the existing stripe element content to force remount
    const stripeCardElement = document.querySelector('#stripe_card_element')
    if (stripeCardElement) {
      // Remove the completion status classes
      stripeCardElement.classList.remove(
        'js-stripe-is-completed',
        'js-stripe-is-not-completed'
      )
      // Clear the inner HTML to unmount the old element
      stripeCardElement.innerHTML = ''
    }

    // Dynamically import the stripe initialization function
    const { initializeStripeElement } = await import(
      '~/common/components/stripe/index'
    )

    // Tell the cart's Stripe setup which intent is being mounted now, before mounting. Its
    // stripe:loaderror listener matches on client_secret, so without this a re-mount Stripe cannot
    // load would be dismissed as some other mount's event and the buyer would be left with an empty
    // card box and no explanation. Already loaded on this page — same module instance.
    const { retargetStripeSetup } = await import(
      '~/site/pages/cart/payment/stripe'
    )
    retargetStripeSetup(clientSecret)

    // Reinitialize the stripe element with the new client secret
    await initializeStripeElement(
      publishableKey,
      clientSecret,
      '#stripe_card_element',
      { automaticPaymentMethods, futureCharge, excludedWallets }
    )
  }

  getSelectedStripeProcessorOption() {
    return document.querySelector(
      '.cart-checkout-payment .payment-processor-option-stripe input[type=radio]:checked, ' +
        ".cart-checkout-payment input[type=hidden][name='payment_method'].payment-processor-option-stripe"
    )
  }

  getBaseCartPath() {
    // Try the form first (checkout pages), then fall back to .cart-page (cart listing page)
    const baseCartPathEl = document.querySelector(
      'form.cart-checkout-payment[data-base-cart-path], [data-base-cart-path]'
    )
    return baseCartPathEl ? baseCartPathEl.dataset.baseCartPath : '/cart'
  }

  showLoading() {
    const loadingEl = document.querySelector('.loading-overlay, .t-loading')
    if (loadingEl) loadingEl.style.display = 'block'
  }

  hideLoading() {
    const loadingEl = document.querySelector('.loading-overlay, .t-loading')
    if (loadingEl) loadingEl.style.display = 'none'
  }

  disableSubmitButtons() {
    document.querySelectorAll('.submit-payment').forEach((button) => {
      button.classList.add('t-btn-disabled', 'js-disabled-for-stripe')
      button.disabled = true
    })
  }

  enableSubmitButtons() {
    document.querySelectorAll('.js-disabled-for-stripe').forEach((button) => {
      button.classList.remove('t-btn-disabled', 'js-disabled-for-stripe')
      button.disabled = false
    })
  }
}
